/**
 * Camera-based QR scanner for the Institution Portal → Scan & Verify.
 *
 * Detection strategy (RLP #7):
 *   1. Native `window.BarcodeDetector` when it exposes qr_code.
 *   2. Fallback to jsQR (pure JS, lazy-loaded) when the native API
 *      is absent — Edge/Chromium on Windows desktop, Firefox, etc.
 *
 * Mount discipline (RLP #7 second correction, 2026-02-23):
 *   The video element is now ALWAYS mounted while the scanner is
 *   active, and stream attachment happens in a `useEffect` that runs
 *   AFTER React has committed the DOM — the previous build attempted
 *   `videoRef.current.srcObject = stream` inside the click handler,
 *   which fired before the `{running && <video/>}` branch had rendered,
 *   so `videoRef.current` was null and the assignment silently
 *   skipped. The preview then rendered a black `<video>` with no
 *   source, `videoWidth` stayed at 0, and jsQR / BarcodeDetector
 *   never saw a real frame. Reordering so the effect owns
 *   attachment + `loadedmetadata` + rAF tick guarantees a live feed
 *   before the first decode attempt.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff, AlertTriangle } from "lucide-react";

async function probeDetection() {
  if (typeof window === "undefined") return "unsupported";
  const hasMedia = !!(navigator?.mediaDevices?.getUserMedia);
  if (!hasMedia) return "unsupported";
  if ("BarcodeDetector" in window) {
    try {
      const formats = await window.BarcodeDetector.getSupportedFormats();
      if (formats.includes("qr_code")) return "native";
    } catch {
      /* fall through */
    }
  }
  return "jsqr";
}

export default function QrCameraScanner({ onScan, testId = "qr-camera-scanner" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectorRef = useRef(null);
  const jsqrRef = useRef(null);
  const onScanRef = useRef(onScan);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    let cancelled = false;
    probeDetection().then((m) => { if (!cancelled) setMode(m); });
    return () => { cancelled = true; };
  }, []);

  const stop = useCallback(() => {
    setRunning(false);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); }
      catch { /* ignore */ }
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => stop(), [stop]);

  const start = async () => {
    setError(null);
    if (mode === "unsupported") {
      setError("This browser cannot access the camera. Paste the token below.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      // Mount the <video/> — actual attachment happens in the effect
      // below once React has committed the element to the DOM.
      setRunning(true);
    } catch (e) {
      const name = e?.name || "";
      let msg = e?.message || "Could not open the camera.";
      if (name === "NotAllowedError") {
        msg = "Camera permission was denied. Enable it in your browser settings, or paste the token below.";
      } else if (name === "NotFoundError") {
        msg = "No camera device was found on this machine. Paste the token below.";
      } else if (name === "NotReadableError") {
        msg = "Another application is using the camera. Close it and try again, or paste the token below.";
      }
      setError(msg);
      stop();
    }
  };

  // Attach the MediaStream and start the decode loop AFTER the
  // <video/> element has been committed to the DOM.
  useEffect(() => {
    if (!running) return undefined;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return undefined;

    let cancelled = false;
    video.srcObject = stream;

    const tick = async () => {
      if (cancelled || !streamRef.current || !videoRef.current) return;
      const v = videoRef.current;
      try {
        if (v.readyState >= 2 && v.videoWidth > 0 && v.videoHeight > 0) {
          let decoded = null;
          if (mode === "native") {
            const codes = await detectorRef.current.detect(v);
            if (codes && codes.length > 0) decoded = String(codes[0].rawValue || "").trim();
          } else if (jsqrRef.current) {
            const w = v.videoWidth;
            const h = v.videoHeight;
            if (!canvasRef.current) canvasRef.current = document.createElement("canvas");
            const canvas = canvasRef.current;
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(v, 0, 0, w, h);
            const img = ctx.getImageData(0, 0, w, h);
            const code = jsqrRef.current(img.data, w, h, { inversionAttempts: "attemptBoth" });
            if (code && code.data) decoded = String(code.data).trim();
          }
          if (decoded) {
            onScanRef.current(decoded);
            stop();
            return;
          }
        }
      } catch { /* per-frame errors ignored */ }
      rafRef.current = requestAnimationFrame(tick);
    };

    const beginTick = () => {
      if (cancelled) return;
      rafRef.current = requestAnimationFrame(tick);
    };

    const onLoaded = async () => {
      if (cancelled) return;
      try {
        if (mode === "native") {
          detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
          beginTick();
        } else {
          if (!jsqrRef.current) {
            const m = await import("jsqr");
            if (cancelled) return;
            jsqrRef.current = m.default || m;
          }
          beginTick();
        }
      } catch (e) {
        if (!cancelled) {
          setError("Could not initialise the QR decoder. Paste the token below.");
          stop();
        }
      }
    };

    // If metadata is already available (fast paths / cached stream)
    // start immediately; otherwise wait for the event.
    if (video.readyState >= 1 && video.videoWidth > 0) {
      onLoaded();
    } else {
      video.addEventListener("loadedmetadata", onLoaded, { once: true });
    }

    // Kick playback. `muted` + `playsInline` are set in JSX so this
    // is allowed by browser autoplay policy after a user gesture.
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => { /* autoplay block ignored */ });
    }

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", onLoaded);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      try { video.srcObject = null; } catch { /* ignore */ }
    };
  }, [running, mode, stop]);

  const buttonLabel =
    mode === null
      ? "Preparing camera…"
      : mode === "unsupported"
      ? "QR scanner unavailable"
      : "Scan with camera";

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        {running ? (
          <Button
            variant="outline"
            size="sm"
            onClick={stop}
            data-testid={`${testId}-stop`}
          >
            <CameraOff className="w-4 h-4 mr-1" /> Stop camera
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={start}
            disabled={mode === "unsupported" || mode === null}
            data-testid={`${testId}-start`}
          >
            <Camera className="w-4 h-4 mr-1" />
            {buttonLabel}
          </Button>
        )}
      </div>
      {running && (
        <div
          className="relative aspect-video max-w-md rounded-lg overflow-hidden border bg-black"
          data-testid={`${testId}-preview`}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-6 border-2 border-emerald-400/60 rounded-lg pointer-events-none" />
          <div className="absolute bottom-2 left-2 text-[10px] text-emerald-200 uppercase tracking-widest bg-black/60 px-2 py-0.5 rounded">
            Hold the citizen's Wallet QR in the frame
          </div>
        </div>
      )}
      {error && (
        <div
          className="text-xs flex items-center gap-1 text-amber-500"
          data-testid={`${testId}-error`}
        >
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
      {mode === "unsupported" && !error && (
        <div className="text-[11px] text-muted-foreground" data-testid={`${testId}-unsupported`}>
          Camera scanning is not available on this device. Paste the token
          below instead.
        </div>
      )}
    </div>
  );
}
